import type { EditFn } from '../scene/useSceneEdit';
import * as viz from '../../domain/visualizerOps';
import { AUDIO_BANDS, MAX_VISUALIZER_FLASH, NOTE_EVENT_CATEGORIES, PULSE_SHAPES, type AvSession, type EventCategory, type VisualizerLayer } from '../../domain/schemas';
import { VISUALIZER_LABELS } from '../../visualizer/layers';
import { CheckControl, ColorControl, Fieldset, NumberControl, SelectControl } from './controls';
import { Alert } from '../ui';

const BAND_LABELS: Record<(typeof AUDIO_BANDS)[number], string> = { full: 'Full (loudness)', low: 'Low (< 250 Hz)', mid: 'Mid (250 Hz – 2 kHz)', high: 'High (> 2 kHz)' };
const CATEGORY_CHOICES: EventCategory[] = [...NOTE_EVENT_CATEGORIES, 'beat', 'bar'];

/**
 * Properties of one visualizer layer. Every change goes through `visualizerOps.updateLayer`
 * (schema-validated, one undo step; continuous edits of one field coalesce).
 */
export function LayerInspector({ session, layer, edit, fallback }: { session: AvSession; layer: VisualizerLayer; edit: EditFn; fallback: boolean }) {
  const set = (field: string, patch: viz.LayerPatch) => edit(`Edit ${layer.name}`, (p) => viz.updateLayer(p, session.id, layer.id, patch), `viz:${layer.id}:${field}`);
  const params = <K extends string>(key: K, value: unknown) => set(`p.${key}`, { params: { [key]: value } } as viz.LayerPatch);
  const r = layer.response;
  const spectrum = layer.type === 'bars' || layer.type === 'radial';
  const mode = r.source === 'measured' ? (fallback ? 'Event-driven fallback (measured analysis unavailable)' : 'Measured audio analysis') : 'Event-driven (synthetic, from musical events)';
  return (
    <div className="flex flex-col gap-3" data-testid="viz-inspector" data-layer={layer.id}>
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-zinc-200">{VISUALIZER_LABELS[layer.type]}</h3>
        <span className={`rounded px-1.5 py-0.5 text-[11px] ${r.source === 'measured' && !fallback ? 'bg-emerald-900/60 text-emerald-200' : 'bg-amber-900/50 text-amber-200'}`} data-testid="viz-mode">
          {mode}
        </span>
      </div>
      <label className="flex flex-col gap-1 text-xs text-zinc-400">
        Name
        <input className="rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm text-zinc-100" value={layer.name} maxLength={40} onChange={(e) => e.target.value.trim() && set('name', { name: e.target.value })} data-testid="viz-name" />
      </label>
      {layer.type === 'pulse' && layer.params.flash > 0 && (
        <Alert title="Flashing effect">This layer flashes the screen on every trigger (strength {layer.params.flash.toFixed(2)} of at most {MAX_VISUALIZER_FLASH}). Flashing can affect photosensitive viewers; set Flash to 0 to turn it off.</Alert>
      )}
      <Fieldset legend="Layer">
        <div className="grid grid-cols-2 gap-2">
          <SelectControl label="Placement" value={layer.placement} options={[['behind', 'Behind scene objects'], ['front', 'In front of the scene']] as const} onChange={(v) => set('placement', { placement: v })} testId="viz-placement" />
          <SelectControl label="Blend" value={layer.blend} options={[['normal', 'Normal'], ['add', 'Add (glow)'], ['screen', 'Screen']] as const} onChange={(v) => set('blend', { blend: v })} testId="viz-blend" />
        </div>
        <CheckControl label="Visible" checked={layer.visible} onChange={(v) => set('visible', { visible: v })} testId="viz-visible" />
        <div className="grid grid-cols-2 gap-2">
          <NumberControl label="X" value={layer.x} min={-4000} max={8000} onChange={(v) => set('x', { x: v })} testId="viz-x" />
          <NumberControl label="Y" value={layer.y} min={-4000} max={8000} onChange={(v) => set('y', { y: v })} testId="viz-y" />
          <NumberControl label="Width" value={layer.width} min={10} max={4000} onChange={(v) => set('width', { width: v })} testId="viz-width" />
          <NumberControl label="Height" value={layer.height} min={10} max={4000} onChange={(v) => set('height', { height: v })} testId="viz-height" />
          <NumberControl label="Scale" value={layer.scale} min={0.1} max={4} step={0.05} onChange={(v) => set('scale', { scale: v })} testId="viz-scale" />
          <NumberControl label="Rotation (°)" value={layer.rotation} min={-360} max={360} onChange={(v) => set('rotation', { rotation: v })} testId="viz-rotation" />
        </div>
        <NumberControl label="Opacity" value={layer.opacity} min={0} max={1} step={0.05} onChange={(v) => set('opacity', { opacity: v })} testId="viz-opacity" />
      </Fieldset>
      <Fieldset legend="Colour">
        <ColorControl label="Primary" value={layer.style.primary} onChange={(v) => set('primary', { style: { primary: v } })} testId="viz-primary" />
        <ColorControl label="Secondary" value={layer.style.secondary} onChange={(v) => set('secondary', { style: { secondary: v } })} testId="viz-secondary" />
        <NumberControl label="Glow" value={layer.style.glow} min={0} max={1} step={0.05} onChange={(v) => set('glow', { style: { glow: v } })} testId="viz-glow" />
      </Fieldset>
      <Fieldset legend="Audio response">
        <SelectControl
          label="Driven by"
          value={r.source}
          options={[
            ['measured', 'Measured audio (analysis)'],
            ['events', 'Musical events (event-driven)'],
          ] as const}
          onChange={(v) => set('source', { response: { source: v } })}
          testId="viz-source"
        />
        {r.source === 'measured' && !spectrum && <SelectControl label="Frequency band" value={r.band} options={AUDIO_BANDS.map((b) => [b, BAND_LABELS[b]] as const)} onChange={(v) => set('band', { response: { band: v } })} testId="viz-band" />}
        {r.source === 'measured' && spectrum && <p className="text-xs text-zinc-400">Bars follow the measured spectrum (32 bands, 30 Hz – 12 kHz).</p>}
        <fieldset className="flex flex-wrap gap-x-3 gap-y-1">
          <legend className="mb-1 text-xs text-zinc-400">{r.source === 'measured' && layer.type !== 'pulse' && layer.type !== 'particles' ? 'Events (used if analysis is unavailable)' : 'Triggering events'}</legend>
          {CATEGORY_CHOICES.map((c) => (
            <label key={c} className="flex items-center gap-1 text-xs text-zinc-300">
              <input
                type="checkbox"
                checked={r.categories.includes(c)}
                onChange={(e) => {
                  const next = e.target.checked ? [...r.categories, c] : r.categories.filter((x) => x !== c);
                  if (next.length) set('categories', { response: { categories: next } });
                }}
                data-testid={`viz-cat-${c}`}
              />
              {c}
            </label>
          ))}
        </fieldset>
        <NumberControl label="Sensitivity" value={r.sensitivity} min={0} max={4} step={0.05} onChange={(v) => set('sensitivity', { response: { sensitivity: v } })} testId="viz-sensitivity" />
        <NumberControl label="Smoothing" value={r.smoothing} min={0} max={1} step={0.05} onChange={(v) => set('smoothing', { response: { smoothing: v } })} testId="viz-smoothing" />
        <NumberControl label="Decay (s)" value={r.decay} min={0} max={2} step={0.05} onChange={(v) => set('decay', { response: { decay: v } })} testId="viz-decay" />
      </Fieldset>
      <Fieldset legend={`${VISUALIZER_LABELS[layer.type]} settings`}>
        <TypeParams layer={layer} params={params} />
      </Fieldset>
    </div>
  );
}

function TypeParams({ layer, params }: { layer: VisualizerLayer; params: (key: string, value: unknown) => void }) {
  switch (layer.type) {
    case 'bars': {
      const p = layer.params;
      return (
        <>
          <SelectControl label="Orientation" value={p.orientation} options={[['horizontal', 'Horizontal (bars rise)'], ['vertical', 'Vertical (bars extend right)']] as const} onChange={(v) => params('orientation', v)} testId="viz-orientation" />
          <NumberControl label="Bars" value={p.count} min={4} max={128} onChange={(v) => params('count', v)} testId="viz-count" />
          <NumberControl label="Spacing" value={p.gap} min={0} max={0.9} step={0.05} onChange={(v) => params('gap', v)} testId="viz-gap" />
          <NumberControl label="Resting height" value={p.minHeight} min={0} max={1} step={0.01} onChange={(v) => params('minHeight', v)} />
          <CheckControl label="Mirrored" checked={p.mirrored} onChange={(v) => params('mirrored', v)} testId="viz-mirrored" />
        </>
      );
    }
    case 'radial': {
      const p = layer.params;
      return (
        <>
          <NumberControl label="Bars" value={p.count} min={8} max={180} onChange={(v) => params('count', v)} testId="viz-count" />
          <NumberControl label="Radius" value={p.innerRadius} min={0} max={0.95} step={0.01} onChange={(v) => params('innerRadius', v)} testId="viz-radius" />
          <NumberControl label="Bar length" value={p.length} min={0.05} max={1} step={0.01} onChange={(v) => params('length', v)} />
          <NumberControl label="Bar width" value={p.barWidth} min={0.1} max={1} step={0.05} onChange={(v) => params('barWidth', v)} />
          <SelectControl label="Direction" value={p.direction} options={[['outward', 'Outward'], ['inward', 'Inward'], ['both', 'Both']] as const} onChange={(v) => params('direction', v)} />
          <NumberControl label="Spin (turns per bar)" value={p.spin} min={-2} max={2} step={0.05} onChange={(v) => params('spin', v)} testId="viz-spin" />
          <NumberControl label="Symmetry" value={p.symmetry} min={1} max={8} onChange={(v) => params('symmetry', v)} />
        </>
      );
    }
    case 'waveform': {
      const p = layer.params;
      return (
        <>
          <NumberControl label="Thickness" value={p.thickness} min={1} max={24} step={0.5} onChange={(v) => params('thickness', v)} testId="viz-thickness" />
          <NumberControl label="Window (ms)" value={p.windowMs} min={10} max={500} onChange={(v) => params('windowMs', v)} />
          <NumberControl label="Points" value={p.points} min={32} max={512} onChange={(v) => params('points', v)} />
          <CheckControl label="Filled" checked={p.filled} onChange={(v) => params('filled', v)} testId="viz-filled" />
          <CheckControl label="Mirrored" checked={p.mirrored} onChange={(v) => params('mirrored', v)} testId="viz-mirrored" />
        </>
      );
    }
    case 'pulse': {
      const p = layer.params;
      return (
        <>
          <SelectControl label="Shape" value={p.shape} options={PULSE_SHAPES.map((s) => [s, s[0]!.toUpperCase() + s.slice(1)] as const)} onChange={(v) => params('shape', v)} testId="viz-shape" />
          <NumberControl label="Rings" value={p.maxRings} min={1} max={12} onChange={(v) => params('maxRings', v)} />
          <NumberControl label="Expansion" value={p.expand} min={0.1} max={2} step={0.05} onChange={(v) => params('expand', v)} />
          <NumberControl label="Line width" value={p.lineWidth} min={1} max={30} onChange={(v) => params('lineWidth', v)} />
          <NumberControl label="Centre shape" value={p.core} min={0} max={1} step={0.05} onChange={(v) => params('core', v)} />
          <NumberControl label="Screen flash" value={p.flash} min={0} max={MAX_VISUALIZER_FLASH} step={0.05} onChange={(v) => params('flash', v)} testId="viz-flash" hint="0 = off · intense" />
        </>
      );
    }
    case 'particles': {
      const p = layer.params;
      return (
        <>
          <NumberControl label="Max particles" value={p.count} min={10} max={600} onChange={(v) => params('count', v)} testId="viz-count" />
          <NumberControl label="Per trigger" value={p.perEvent} min={0} max={64} onChange={(v) => params('perEvent', v)} />
          <NumberControl label="Continuous rate (/s)" value={p.rate} min={0} max={120} onChange={(v) => params('rate', v)} />
          <NumberControl label="Lifetime (s)" value={p.lifetime} min={0.2} max={6} step={0.1} onChange={(v) => params('lifetime', v)} />
          <NumberControl label="Speed" value={p.speed} min={0} max={800} onChange={(v) => params('speed', v)} />
          <NumberControl label="Direction (°)" value={p.direction} min={-180} max={180} onChange={(v) => params('direction', v)} />
          <NumberControl label="Spread (°)" value={p.spread} min={0} max={360} onChange={(v) => params('spread', v)} />
          <NumberControl label="Gravity" value={p.gravity} min={-1000} max={1000} onChange={(v) => params('gravity', v)} />
          <NumberControl label="Size" value={p.size} min={1} max={20} step={0.5} onChange={(v) => params('size', v)} />
        </>
      );
    }
    case 'grid': {
      const p = layer.params;
      return (
        <>
          <NumberControl label="Lines" value={p.lines} min={4} max={40} onChange={(v) => params('lines', v)} />
          <NumberControl label="Rows per beat" value={p.speed} min={0} max={4} step={0.05} onChange={(v) => params('speed', v)} />
          <NumberControl label="Horizon" value={p.horizon} min={0} max={0.9} step={0.01} onChange={(v) => params('horizon', v)} />
          <NumberControl label="Beat glow" value={p.pulse} min={0} max={1} step={0.05} onChange={(v) => params('pulse', v)} />
        </>
      );
    }
  }
}
